import React, { useState, useMemo, useEffect } from 'react';
import { X, CalendarOff, Plus, Trash2, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatApiError } from '../utils/apiErrors';
import { isStaffActiveOnDate, getStaffWageTypeOnDate } from '../utils/payroll';
import UiDateField from './ui/UiDateField';

const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const eachDay = (start, end) => {
  const out = [];
  let d = new Date(`${start}T12:00:00`);
  const last = new Date(`${end}T12:00:00`);
  while (d <= last) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    d.setDate(d.getDate() + 1);
  }
  return out;
};

// Off-day types feed the unified Performance path.
const OFFDAY_TYPES = [
  { value: 'absentee', label: 'Absentee (Unauthorized)', defaultDeduct: true },
  { value: 'leave', label: 'Leave (Authorized)', defaultDeduct: false },
  { value: 'holiday', label: 'Holiday', defaultDeduct: false },
];

// Enumerated reasons per off-day type. Descriptive detail goes in the separate Notes field.
const REASON_OPTIONS = {
  leave: ['Emergency', 'Monthly', 'Others'],
  absentee: ['Unreachable', 'Late Notification', 'Others'],
  holiday: ['Public Holiday', 'Company Holiday', 'Others'],
};

const LEGACY_LEAVE_TYPE_TO_REASON = { sick: 'Others', casual: 'Others', annual: 'Others', unpaid: 'Others', other: 'Others' };

const staffName = (s) => s?.full_name || `${s?.first_name || ''} ${s?.last_name || ''}`.trim();

/**
 * AddLeaveModal — records or edits an off-day (Leave / Holiday / Absentee) on the unified
 * Performance path. Date leads the form (it decides which staff were active and their wage type).
 * In add-mode you can Apply several entries into a queue, then Save all at once.
 */
export default function AddLeaveModal({ staff = [], defaultDate, editRecord = null, onClose, onSaved }) {
  const isEdit = !!editRecord;
  const isLegacyLeave = editRecord?._source === 'leave';

  const initial = useMemo(() => {
    if (!editRecord) {
      return { staff: '', off_type: 'absentee', deduct: true, reason: '', notes: '',
        start_date: defaultDate || getLocalDateString(), end_date: defaultDate || getLocalDateString() };
    }
    if (editRecord._source === 'leave') {
      return {
        staff: String(editRecord.staff || ''),
        off_type: editRecord.is_absent ? 'absentee' : 'leave',
        deduct: !editRecord.is_paid,
        reason: LEGACY_LEAVE_TYPE_TO_REASON[editRecord.leave_type] || '',
        notes: editRecord.reason || '',
        start_date: editRecord.start_date || defaultDate || getLocalDateString(),
        end_date: editRecord.end_date || editRecord.start_date || defaultDate || getLocalDateString(),
      };
    }
    const d = String(editRecord.date || '').substring(0, 10) || getLocalDateString();
    return {
      staff: String(editRecord.staff || ''),
      off_type: editRecord.violation_type || 'leave',
      deduct: !editRecord.is_warning,
      reason: editRecord.reason || '',
      notes: editRecord.description || '',
      start_date: d, end_date: d,
    };
  }, [editRecord, defaultDate]);

  const [form, setForm] = useState(initial);
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  // Staff available on the chosen start date, plus their wage type as it was then.
  const staffOptions = useMemo(
    () => [...staff]
      .filter((s) => isStaffActiveOnDate(s, form.start_date) || String(s.id) === String(form.staff))
      .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')),
    [staff, form.start_date, form.staff]
  );
  const selected = useMemo(() => staff.find((s) => String(s.id) === String(form.staff)), [staff, form.staff]);
  const wageOnDate = selected ? getStaffWageTypeOnDate(selected, form.start_date) : null;

  const setType = (t) => {
    const meta = OFFDAY_TYPES.find((o) => o.value === t);
    setForm((f) => ({ ...f, off_type: t, deduct: t === 'holiday' ? false : meta.defaultDeduct, reason: '' }));
  };

  const reasonChoices = REASON_OPTIONS[form.off_type] || [];

  const validate = () => {
    if (!form.staff) { toast.error('Select a staff member'); return false; }
    if (!form.start_date || !form.end_date) { toast.error('Enter start and end date'); return false; }
    if (eachDay(form.start_date, form.end_date).length === 0) { toast.error('End date is before start date'); return false; }
    return true;
  };

  // Queue the current entry (add-mode only) so several off-days can be committed together.
  const applyEntry = () => {
    if (!validate()) return;
    setPending((prev) => [...prev, { ...form }]);
    // Keep the date & type context; clear staff/reason/notes for the next entry.
    setForm((f) => ({ ...f, staff: '', reason: '', notes: '' }));
  };

  const commitOne = async (entry) => {
    const is_warning = !entry.deduct;
    const days = eachDay(entry.start_date, entry.end_date);
    await Promise.all(days.map((d) => api.post('/staff-performance/', {
      staff: entry.staff, violation_type: entry.off_type, date: d, is_warning,
      reason: entry.reason || '', description: entry.notes || '',
    })));
    return days.length;
  };

  const save = async () => {
    // Edit-mode: single record update.
    if (isEdit) {
      if (!validate()) return;
      setSaving(true);
      try {
        const is_warning = !form.deduct;
        const days = eachDay(form.start_date, form.end_date);
        const fields = { violation_type: form.off_type, is_warning, reason: form.reason || '', description: form.notes || '' };
        if (isLegacyLeave) {
          await Promise.all(days.map((d) => api.post('/staff-performance/', { staff: form.staff, date: d, ...fields })));
          await api.delete(`/staff-leaves/${editRecord.id}/`);
        } else {
          await api.patch(`/staff-performance/${editRecord.id}/`, { ...fields, date: days[0] });
          await Promise.all(days.slice(1).map((d) => api.post('/staff-performance/', { staff: form.staff, date: d, ...fields })));
        }
        toast.success('Off-day updated');
        onSaved && onSaved();
        onClose && onClose();
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to save off-day'));
      } finally {
        setSaving(false);
      }
      return;
    }
    // Add-mode: commit the queue (plus the current form if it's filled in).
    const entries = [...pending];
    if (form.staff) { if (!validate()) return; entries.push({ ...form }); }
    if (entries.length === 0) { toast.error('Add at least one entry'); return; }
    setSaving(true);
    try {
      let total = 0;
      for (const entry of entries) total += await commitOne(entry);
      toast.success(`${total} day${total !== 1 ? 's' : ''} recorded`);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save off-days'));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border rounded-lg';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const effectLabel = (deduct) => form.off_type === 'absentee' ? (deduct ? 'Deduct' : 'Warning') : (deduct ? 'Unpaid' : 'Paid');
  const typeLabelOf = (t) => OFFDAY_TYPES.find((o) => o.value === t)?.label || t;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><CalendarOff size={18} /> {isEdit ? 'Edit Off-Day' : 'Add Off-Day'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Date leads — it determines who was employed and their wage type on the day. */}
          <div>
            <label className={labelCls}>Start Date *</label>
            <UiDateField value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v, ...(form.end_date < v ? { end_date: v } : {}) })} />
          </div>
          <div>
            <label className={labelCls}>End Date *</label>
            <UiDateField value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} />
          </div>
          <div>
            <label className={labelCls}>Staff *</label>
            <select value={form.staff} onChange={(e) => setForm({ ...form, staff: e.target.value })} className={inputCls} required disabled={isEdit}>
              <option value="">Select staff</option>
              {staffOptions.map((s) => (
                <option key={s.id} value={s.id}>{staffName(s)}{getStaffWageTypeOnDate(s, form.start_date) === 'daily_wages' ? ' · Daily' : ''}</option>
              ))}
            </select>
            {selected && (
              <p className="text-[11px] text-gray-500 mt-1">
                On {form.start_date}: <span className="font-medium">{wageOnDate === 'daily_wages' ? 'Daily-wage' : 'Salaried'}</span>
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>Type *</label>
            <select value={form.off_type} onChange={(e) => setType(e.target.value)} className={inputCls}>
              {OFFDAY_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Salary Effect</label>
            <select value={form.deduct ? 'deduct' : 'warning'} onChange={(e) => setForm({ ...form, deduct: e.target.value === 'deduct' })} className={inputCls}>
              <option value="deduct">{effectLabel(true)}</option>
              <option value="warning">{effectLabel(false)}</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Reason</label>
            <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={inputCls}>
              <option value="">— Select reason —</option>
              {reasonChoices.map((r) => <option key={r} value={r}>{r}</option>)}
              {form.reason && !reasonChoices.includes(form.reason) && <option value={form.reason}>{form.reason}</option>}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} rows="2" placeholder="Descriptive notes (optional)" />
          </div>
          {!isEdit && (
            <div className="md:col-span-2">
              <button type="button" onClick={applyEntry} className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
                <Plus size={14} /> Apply (add to list)
              </button>
            </div>
          )}
        </div>

        {!isEdit && pending.length > 0 && (
          <div className="px-4 pb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Queued ({pending.length})</p>
            <div className="border rounded-lg divide-y overflow-hidden max-h-52 overflow-y-auto">
              {pending.map((e, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                  <span className="truncate">
                    <span className="font-medium">{staffName(staff.find((s) => String(s.id) === String(e.staff)))}</span>
                    {' · '}{typeLabelOf(e.off_type)}{' · '}{effectLabel(e.deduct)}
                    {' · '}{e.start_date}{e.end_date !== e.start_date ? ` → ${e.end_date}` : ''}
                    {e.reason ? ` · ${e.reason}` : ''}
                  </span>
                  <button onClick={() => setPending((prev) => prev.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 ml-2"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {saving ? 'Saving…' : <><Check size={14} /> {isEdit ? 'Save Changes' : `Save${pending.length ? ` (${pending.length + (form.staff ? 1 : 0)})` : ''}`}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
